import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import {
    Box,
    Button,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import React from 'react';

import { MessageKey, useT } from '../../i18n';
import { formatClock, Mode, Status } from '../timer';

const pulse = keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.04); }
`;

const MODES: { id: Mode; label: MessageKey }[] = [
    { id: 'focus', label: 'timer.focus' },
    { id: 'short', label: 'timer.short_other' },
    { id: 'long', label: 'timer.long' },
    { id: 'roulette', label: 'timer.roulette' },
];

interface ControlsProps {
    running: boolean;
    status: Status;
    celebrating: boolean;
    color: string;
    onDarkScene: boolean;
    onToggle: () => void;
    onReset: () => void;
    onSkip: () => void;
}

/**
 * The buttons. Memoized so they do not re-render on every timer tick: a tooltip anchored to a
 * button that re-renders ten times a second can flicker on a GPU-composited page.
 */
const Controls = React.memo<ControlsProps>(function Controls({
    running,
    status,
    celebrating,
    color,
    onDarkScene,
    onToggle,
    onReset,
    onSkip,
}) {
    const t = useT();
    const iconSx = onDarkScene
        ? {
              color: '#fff',
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
          }
        : undefined;
    return (
        <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title={t('timer.reset')}>
                <span>
                    <IconButton
                        onClick={onReset}
                        sx={iconSx}
                        disabled={celebrating || status === 'idle'}
                        aria-label={t('timer.resetAria')}
                    >
                        <RestartAltRoundedIcon />
                    </IconButton>
                </span>
            </Tooltip>
            <Button
                variant="contained"
                size="large"
                onClick={onToggle}
                disabled={celebrating}
                startIcon={
                    running ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />
                }
                sx={{
                    minWidth: 170,
                    height: 60,
                    fontSize: 22,
                    boxShadow: (t) =>
                        `0 6px 0 ${alpha(t.palette.primary.dark, 0.9)}, 0 12px 24px ${alpha(color, 0.45)}`,
                    transition: 'transform 0.08s, box-shadow 0.08s',
                    '&:active': {
                        transform: 'translateY(5px)',
                        boxShadow: (t) => `0 1px 0 ${t.palette.primary.dark}`,
                    },
                }}
            >
                {running
                    ? t('timer.pause')
                    : status === 'paused'
                      ? t('timer.resume')
                      : t('timer.start')}
            </Button>
            <Tooltip title={t('timer.skip')}>
                <span>
                    <IconButton
                        onClick={onSkip}
                        sx={iconSx}
                        disabled={celebrating}
                        aria-label={t('timer.skipAria')}
                    >
                        <SkipNextRoundedIcon />
                    </IconButton>
                </span>
            </Tooltip>
        </Stack>
    );
});

interface Props {
    mode: Mode;
    status: Status;
    remainingMs: number;
    progress: number;
    color: string;
    /** The scene behind is dark (night, or space): the clock and icons turn light. */
    onDarkScene?: boolean;
    celebrating: boolean;
    /** Length of the short break, shown in its label in languages that say it ("Só 5 minutinhos"). */
    shortMinutes: number;
    onMode: (m: Mode) => void;
    onToggle: () => void;
    onReset: () => void;
    onSkip: () => void;
}

export const TimerPanel: React.FC<Props> = ({
    mode,
    status,
    remainingMs,
    progress,
    color,
    onDarkScene = false,
    celebrating,
    shortMinutes,
    onMode,
    onToggle,
    onReset,
    onSkip,
}) => {
    const t = useT();
    const running = status === 'running';
    return (
        <Stack
            alignItems="center"
            spacing={1.5}
            sx={{
                width: '100%',
                maxWidth: 440,
                mx: 'auto',
                px: 2,
            }}
        >
            <Box
                sx={{
                    maxWidth: '100%',
                    overflowX: 'auto',
                    display: 'flex',
                    borderRadius: 999,
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                }}
            >
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={mode}
                    disabled={celebrating}
                    onChange={(_, v: Mode | null) => v && onMode(v)}
                    aria-label={t('timer.mode')}
                    sx={{
                        mx: 'auto',
                        flexWrap: 'nowrap',
                        bgcolor: (t) => alpha(t.palette.background.paper, 0.92),
                        borderRadius: 999,
                        '& .MuiToggleButton-root': {
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            borderRadius: 999,
                            px: 1.75,
                            fontWeight: 800,
                            textTransform: 'none',
                        },
                    }}
                >
                    {MODES.map((m) => (
                        <ToggleButton key={m.id} value={m.id}>
                            {m.id === 'short'
                                ? t('timer.short' as MessageKey, {
                                      count: shortMinutes,
                                      minutes: shortMinutes,
                                  })
                                : t(m.label)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            <Typography
                component="div"
                aria-live="off"
                sx={{
                    fontSize: { xs: 64, sm: 84 },
                    lineHeight: 1,
                    fontWeight: 800,
                    letterSpacing: 2,
                    fontVariantNumeric: 'tabular-nums',
                    color: onDarkScene ? '#fff' : undefined,
                    textShadow: `0 4px 0 ${alpha(color, 0.35)}`,
                    animation:
                        running && progress > 0.9
                            ? `${pulse} 0.5s ease-in-out infinite`
                            : undefined,
                }}
            >
                {mode === 'roulette'
                    ? t('timer.unknownClock')
                    : formatClock(remainingMs)}
            </Typography>

            <Box
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress * 100)}
                sx={{
                    width: '100%',
                    height: 14,
                    borderRadius: 999,
                    bgcolor: (t) => alpha(t.palette.text.primary, 0.12),
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        height: '100%',
                        width: `${progress * 100}%`,
                        borderRadius: 999,
                        background: `linear-gradient(90deg, ${color}, #ffd93d)`,
                        transition: 'width 0.3s linear',
                        boxShadow: `0 0 14px ${alpha(color, 0.8)}`,
                    }}
                />
            </Box>

            <Controls
                running={running}
                status={status}
                celebrating={celebrating}
                color={color}
                onDarkScene={onDarkScene}
                onToggle={onToggle}
                onReset={onReset}
                onSkip={onSkip}
            />
        </Stack>
    );
};
